import type { CanvasObject } from '../types';
import type { Artboard } from '../hooks/useCanvasTransform';

/**
 * 회전 반영 AABB 계산
 * 객체의 회전 각도를 고려하여 실제 바운딩 박스를 계산합니다.
 */
export function getRotatedAABB(obj: CanvasObject): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  centerX: number;
  centerY: number;
} {
  const angle = obj.rotation || 0;
  if (Math.abs(angle) < 1e-6) {
    return { 
      minX: obj.x, 
      minY: obj.y, 
      maxX: obj.x + obj.width, 
      maxY: obj.y + obj.height,
      centerX: obj.x + obj.width / 2,
      centerY: obj.y + obj.height / 2
    };
  }
  const cx = obj.x + obj.width / 2;
  const cy = obj.y + obj.height / 2;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const corners = [
    { x: obj.x, y: obj.y },
    { x: obj.x + obj.width, y: obj.y },
    { x: obj.x + obj.width, y: obj.y + obj.height },
    { x: obj.x, y: obj.y + obj.height },
  ].map(p => {
    const dx = p.x - cx;
    const dy = p.y - cy;
    return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
  });
  const xs = corners.map(c => c.x);
  const ys = corners.map(c => c.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return { 
    minX, 
    minY, 
    maxX, 
    maxY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2
  };
}

/**
 * 정렬 기준 영역 계산
 * - 단일 선택: 객체의 artboardId가 있으면 해당 아트보드 기준, 없으면 (0, 0) 기준
 * - 다중 선택: 선택된 객체들의 바운딩 박스 기준
 */
export function calculateAlignBounds(
  selectedObjects: CanvasObject[],
  artboardsMap: Map<string, Artboard>
): { x: number; y: number; width: number; height: number } {
  if (selectedObjects.length === 1) {
    const obj = selectedObjects[0];
    if (obj.artboardId) {
      const ab = artboardsMap.get(obj.artboardId);
      if (ab) {
        return { x: ab.x, y: ab.y, width: ab.width, height: ab.height };
      }
    }
    return { x: 0, y: 0, width: 0, height: 0 };
  } else {
    const aabbs = selectedObjects.map(getRotatedAABB);
    const minX = Math.min(...aabbs.map(b => b.minX));
    const minY = Math.min(...aabbs.map(b => b.minY));
    const maxX = Math.max(...aabbs.map(b => b.maxX));
    const maxY = Math.max(...aabbs.map(b => b.maxY));
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
}

